import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import InvitationView from "../invitation/InvitationView";
import {
  EDITOR_MESSAGES,
  PREVIEW_MESSAGES,
  postToEditor,
  readEditorMessage,
} from "../invitation/previewProtocol";
import {
  clearPreviewFocus,
  focusPendingSection,
  focusSection,
  lineBeingTyped,
} from "../invitation/previewFocus";
import { templateIdOf } from "../invitation/registry";
import { slotDefaults } from "../invitation/slots";
import { rebindWebflow, reinitWebflow, revealInvitation } from "../invitation/webflow";
import "../invitation/preview.css";

// The editor speaks short codes; i18next is set up with the long ones.
const LANGUAGES = { en: "enUS", el: "elGR" };
// After the set of sections changes, the Webflow runtime is re-bound once the
// couple pauses — never per keystroke, and never behind a loading state.
const REBIND_DELAY_MS = 250;

/** What a `render` message asks for, or null when it can't be drawn. */
function readRender(message) {
  const { payload, lang, editable } = message;
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
  return { payload, lang: lang === "el" ? "el" : "en", editable: editable === true };
}

/**
 * The couple's invitation inside their editor (app/, /couple/invitation),
 * which frames this page — INVITATION-EDITOR-SPEC.md §5.
 *
 * The editor owns the data: it sends the invitation in (`render`, unsaved
 * edits included) and this page draws it with the same InvitationView guests
 * see. It never fetches, never checks readiness (missing details show as
 * placeholders), loads no tags and caches no language (see main.jsx, i18n.js
 * and index.html). Links lead nowhere from here; clicking a section, or typing
 * on one of its lines, tells the editor instead.
 */
const Preview = () => {
  const { t, i18n } = useTranslation();
  const [framed] = useState(() => window.parent !== window);
  const [view, setView] = useState(null);
  const layoutRef = useRef(null);
  const rebindTimer = useRef(null);
  const defaultsRef = useRef(null);
  const wasEditingRef = useRef(false);

  // Listen to the editor first, then tell it we're here.
  useEffect(() => {
    if (!framed) return undefined;
    const onMessage = (e) => {
      const message = readEditorMessage(e);
      if (!message) return;
      if (message.type === EDITOR_MESSAGES.RENDER) {
        const next = readRender(message);
        if (!next) return;
        // Applied before the draw so the page never shows one frame in the
        // old language. i18n.js keeps it out of localStorage on this page.
        if (i18n.resolvedLanguage !== LANGUAGES[next.lang]) {
          i18n.changeLanguage(LANGUAGES[next.lang]);
        }
        setView(next);
      } else if (message.type === EDITOR_MESSAGES.FOCUS_SECTION) {
        if (typeof message.section === "string") focusSection(message.section);
      }
    };
    window.addEventListener("message", onMessage);
    // A frame the editor hid (phones, while the couple edits) is resized when
    // it is shown again; that is when a section asked for meanwhile can be found.
    window.addEventListener("resize", focusPendingSection);
    postToEditor(PREVIEW_MESSAGES.READY);
    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("resize", focusPendingSection);
    };
  }, [framed, i18n]);

  useEffect(
    () => () => {
      clearTimeout(rebindTimer.current);
      clearPreviewFocus();
    },
    [],
  );

  const handleRendered = useCallback(({ sections, layoutKey }) => {
    postToEditor(PREVIEW_MESSAGES.RENDERED, { sections });
    if (layoutRef.current === null) {
      layoutRef.current = layoutKey;
      reinitWebflow();
      revealInvitation();
      return;
    }
    if (layoutRef.current === layoutKey) return;
    layoutRef.current = layoutKey;
    clearTimeout(rebindTimer.current);
    rebindTimer.current = setTimeout(rebindWebflow, REBIND_DELAY_MS);
  }, []);

  // The template's own lines, whenever the language, event type or template
  // they are worded for changes — the editor offers them as the starting point.
  useEffect(() => {
    if (!view) return;
    const templateId = templateIdOf(view.payload.invitation);
    const eventType = view.payload.eventType || "wedding";
    const key = `${view.lang}|${eventType}|${templateId}`;
    if (defaultsRef.current === key) return;
    defaultsRef.current = key;
    postToEditor(PREVIEW_MESSAGES.DEFAULTS, {
      lang: view.lang,
      eventType,
      texts: slotDefaults(i18n.getFixedT(LANGUAGES[view.lang]), templateId, eventType),
    });
  }, [view, i18n]);

  const handleTextInput = useCallback((slot, value) => {
    postToEditor(PREVIEW_MESSAGES.TEXT_INPUT, { slot, value });
  }, []);

  // A click that ends typing on a line (focus has already left it by the time
  // `click` fires) is not a request to open a section. A click that brings the
  // couple back from the editor is: the line the frame kept focused meanwhile
  // isn't being typed on.
  const handlePointerDownCapture = () => {
    wasEditingRef.current = !!lineBeingTyped();
  };

  const handleClickCapture = (e) => {
    const target = e.target instanceof Element ? e.target : e.target.parentElement;
    if (!target || target.closest(".navbar")) return;
    // Links lead nowhere from the preview — the RSVP button, map pins,
    // phone numbers, the registry. Stopping the click here also keeps the
    // RSVP dialog shut.
    if (target.closest("a[href]")) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (wasEditingRef.current || target.closest("[data-invitation-slot]")) return;
    const section = target.closest("[data-invitation-section]");
    if (section) {
      postToEditor(PREVIEW_MESSAGES.SELECT_SECTION, {
        section: section.dataset.invitationSection,
      });
    }
  };

  if (!framed) {
    return (
      <div className="page-wrapper invitation-preview">
        <header className="section _60">
          <div className="container hero-center realisa">
            <p className="made-28">{t("preview.notFramed")}</p>
          </div>
        </header>
      </div>
    );
  }

  return (
    <div
      className="page-wrapper invitation-preview"
      onPointerDownCapture={handlePointerDownCapture}
      onClickCapture={handleClickCapture}
    >
      {view && (
        <InvitationView
          event={view.payload}
          preview
          editable={view.editable}
          onTextInput={handleTextInput}
          onRendered={handleRendered}
        />
      )}
    </div>
  );
};

export default Preview;
